import type { ApprovalIntake } from '@kop/contracts';
import type { TbmRecord } from '@kop/tbm';
import type { EducationRecord } from '@kop/safety-education';
import type { PrintViewModel } from '@kop/print';
import type {VictimApprovalReport} from '@kop/incidents-emergency';

export type ApprovalScope =
  | { readonly kind: 'tenant'; readonly tenantId: string }
  | { readonly kind: 'site'; readonly tenantId: string; readonly siteId: string }
  | {
      readonly kind: 'contractor';
      readonly tenantId: string;
      readonly siteId: string;
      readonly contractorId: string;
    };

export type ApprovalRequestStatus =
  'requested' | 'reviewing' | 'returned' | 'approved' | 'withdrawn';

export const REVIEW_APPROVAL_NOW = '2026-08-18T12:00:00.000+09:00';

export type ApprovalInboxFilter =
  'my-pending' | 'requested-by-me' | 'completed' | 'returned' | 'overdue' | 'all';

export type ApprovalLineRole = 'requester' | 'reviewer' | 'approver';

/** A revision reference is a pointer only. The source domain continues to own every content byte. */
export type ImmutableSourceRevisionRef = {
  readonly sourceDomain: string;
  readonly documentKind: string;
  readonly documentId: string;
  readonly revisionId: string;
  readonly revisionNumber: number;
  readonly contentHash: string;
  readonly algorithm: 'sha256';
  readonly scope: ApprovalScope;
};

export function requiresVictimApprovalReport(source:Pick<ImmutableSourceRevisionRef,'sourceDomain'|'documentKind'>) {
  return source.sourceDomain==='victim-registry' && source.documentKind==='victim-report';
}

export type ApprovalLineMember = {
  readonly lineMemberId: string;
  readonly sequence: number;
  readonly role: ApprovalLineRole;
  readonly identityId: string;
  readonly displayName: string;
  readonly dutyLabel: string;
};

export type ApprovalRequest = {
  readonly inspectionReportBindingId?: string;
  readonly requestId: string;
  readonly round: number;
  readonly previousRequestId: string | null;
  readonly source: ImmutableSourceRevisionRef;
  readonly eventKind: string;
  readonly titleSnapshot: string;
  readonly requestedByIdentityId: string;
  readonly requestedByName: string;
  readonly requestedAt: string;
  readonly dueAt: string;
  readonly status: ApprovalRequestStatus;
  readonly activeSequence: number | null;
  readonly line: readonly ApprovalLineMember[];
  readonly policyId: string;
  readonly policyVersion: number;
};

export type ApprovalPolicy = {
  readonly policyId: string;
  readonly version: number;
  readonly sourceDomain: string;
  readonly documentKind: string;
  readonly eventKind: string;
  readonly scopeKinds: readonly ApprovalScope['kind'][];
  readonly mode: 'none' | 'line-approval';
  readonly prohibitSelfApproval: boolean;
  readonly requireDistinctReviewerAndApprover: boolean;
  readonly dueHours: number | null;
  readonly validFrom: string;
  readonly validUntil: string | null;
};

export type DelegationAuthority = {
  readonly delegationId: string;
  readonly evidenceId: string;
  readonly delegatorIdentityId: string;
  readonly delegateIdentityId: string;
  readonly allowedRoles: readonly Exclude<ApprovalLineRole, 'requester'>[];
  readonly sourceDomains: readonly string[];
  readonly documentKinds: readonly string[];
  readonly eventKinds: readonly string[];
  readonly scope: ApprovalScope;
  readonly validFrom: string;
  readonly validUntil: string;
  readonly revokedAt: string | null;
};

/** Result of a trusted source adapter re-reading the exact immutable revision. */
export type SourceAuthorityReceipt = {
  readonly receiptId: string;
  readonly verified: true;
  readonly source: ImmutableSourceRevisionRef;
  readonly issuedAt: string;
  readonly expiresAt: string;
};

/** Server-selected identity and scope. A browser role label is never authority. */
export type DecisionAuthorityReceipt = {
  readonly receiptId: string;
  readonly identityId: string;
  readonly requestId: string;
  readonly lineMemberId: string;
  readonly role: Exclude<ApprovalLineRole, 'requester'>;
  readonly scope: ApprovalScope;
  readonly delegation: DelegationAuthority | null;
  readonly issuedAt: string;
  readonly expiresAt: string;
};

export type ApprovalDecision = {
  readonly commandId: string;
  readonly idempotencyKey: string;
  readonly requestId: string;
  readonly action: 'approve' | 'return';
  readonly reason: string | null;
  readonly actorIdentityId: string;
  readonly expectedRevisionId: string;
  readonly expectedContentHash: string;
  readonly expectedPolicyId: string;
  readonly expectedPolicyVersion: number;
  readonly sourceAuthorityReceiptId: string;
  readonly decisionAuthorityReceiptId: string;
};

export type ApprovalAuditReceipt = {
  readonly receiptId: string;
  readonly requestId: string;
  readonly commandId: string;
  readonly idempotencyKey: string;
  readonly action: ApprovalDecision['action'];
  readonly actorIdentityId: string;
  readonly actedAsIdentityId: string;
  readonly delegationId: string | null;
  readonly sourceRevisionId: string;
  readonly sourceContentHash: string;
  readonly policyId: string;
  readonly policyVersion: number;
  readonly previousStatus: ApprovalRequestStatus;
  readonly resultingStatus: ApprovalRequestStatus;
  readonly recordedAt: string;
  readonly serverSequence: number;
  readonly auditEventId: string;
};

export type ApprovalPreview = {
  readonly source: ImmutableSourceRevisionRef;
  readonly title: string;
  readonly subtitle: string | null;
  readonly fields: readonly { readonly label: string; readonly value: string }[];
  readonly warning: string | null;
};

export type ApprovalHubQuery = {
  readonly scope: ApprovalScope;
  readonly filter: ApprovalInboxFilter;
  readonly actorIdentityId: string;
};

export type ApprovalAllowedAction = 'withdraw' | 'resubmit' | 'decide' | 'return';

export type ApprovalWorkspaceRow = {
  readonly requestId: string;
  readonly version: number;
  readonly status: ApprovalRequestStatus;
  readonly title: string;
  readonly sourceDomain: string;
  readonly documentKind: string;
  readonly revisionNumber: number;
  readonly requestedBy: { readonly identityId: string; readonly displayName: string };
  readonly requestedAt: string;
  readonly dueAt: string;
  readonly activeStep: {
    readonly sequence: number;
    readonly role: 'reviewer' | 'approver';
    readonly displayName: string;
    readonly delegated: boolean;
    readonly delegationId: string | null;
  } | null;
  readonly actions: readonly ApprovalAllowedAction[];
};

export type ApprovalWorkspacePage = {
  readonly rows: readonly ApprovalWorkspaceRow[];
  readonly nextCursor: { readonly requestedAt: string; readonly requestId: string } | null;
  readonly completeness: 'complete' | 'more';
};

export type ApprovalHistoryEvent = {
  readonly eventId: string;
  readonly kind: 'submitted' | 'approved-stage' | 'returned' | 'withdrawn' | 'approved';
  readonly actorName: string;
  readonly reason: string | null;
  readonly recordedAt: string;
  readonly resultingStatus: ApprovalRequestStatus;
  readonly serverSequence: number;
};

export type ApprovalDocument = {
  readonly remediationReport?: RemediationReport | null;
  readonly request: ApprovalRequest;
  readonly version: number;
  readonly allowedActions: readonly ApprovalAllowedAction[];
  readonly history: readonly ApprovalHistoryEvent[];
  readonly resubmissionSource: ImmutableSourceRevisionRef | null;
  readonly resubmissionIntake?: ApprovalIntake | null;
  readonly lineMode: 'requester' | 'server-resolved';
  readonly delegationId: string | null;
  /** The server-selected immutable route revision currently governing this request. */
  readonly effectiveRouteRevision: number;
  /** True only when the server verified this viewer is the current safety-manager reviewer. */
  readonly canExtendRoute: boolean;
  readonly requireSignatureUse: boolean;
  readonly presentation: Readonly<{
    title: string;
    subtitle: string | null;
    sections: readonly Readonly<Record<string, unknown>>[];
    canonicalSnapshot?: unknown;
    canonicalSnapshotHash?: string;
  }> | null;
  readonly evidenceReceipts: readonly Readonly<Record<string, unknown>>[];
  readonly tbmReport?: Readonly<{ record: TbmRecord; contentHash: string; recordHash: string }> | null;
  readonly victimReport?:VictimApprovalReport|null;
  readonly educationReport?: Readonly<{
    record: EducationRecord;
    report?: PrintViewModel;
    media: readonly Readonly<{ mediaManifestId: string; evidenceId: string; contentHash: string; caption: string }>[];
  }> | null;
};

export type ApprovalSubmissionReceipt = {
  readonly receiptId: string;
  readonly requestId: string;
  readonly commandId: string;
  readonly idempotencyKey: string;
  readonly status: 'requested';
  readonly recordedAt: string;
  readonly serverSequence: number;
  readonly source: ImmutableSourceRevisionRef;
  readonly immutable: true;
};

export type ApprovalWithdrawalReceipt = {
  readonly receiptId: string;
  readonly requestId: string;
  readonly commandId: string;
  readonly idempotencyKey: string;
  readonly status: 'withdrawn';
  readonly reason: string;
  readonly recordedAt: string;
  readonly serverSequence: number;
  readonly source: ImmutableSourceRevisionRef;
  readonly immutable: true;
};

export type ApprovalRouteExtensionReceipt = {
  readonly receiptId: string;
  readonly requestId: string;
  readonly routeRevisionId: string;
  readonly previousRouteRevisionId: string | null;
  readonly revision: number;
  readonly reason: string;
  readonly addedReviewerIdentityIds: readonly string[];
  readonly actorIdentityId: string;
  readonly sourceRevisionId: string;
  readonly sourceContentHash: string;
  readonly recordedAt: string;
  readonly immutable: true;
};

export type ApprovalLineSelection = readonly {
  readonly identityId: string;
  readonly groupId: string;
}[];

export type ApprovalIntakeDraft = Readonly<{
  intakeId: string;
  title: string;
  sourceDomain: string;
  documentKind: string;
  recordedAt: string;
  scope: ApprovalScope;
}>;
export type ApprovalIntakeDraftCursor = Readonly<{ recordedAt: string; documentId: string }>;
export type ApprovalIntakeDraftPage = Readonly<{
  drafts: readonly ApprovalIntakeDraft[];
  nextCursor: ApprovalIntakeDraftCursor | null;
}>;

export type ApprovalDirectoryPerson = {
  readonly identityId: string;
  readonly displayName: string;
  readonly groupId: string;
  readonly organizationPath: readonly string[];
  readonly contractorId: string | null;
  readonly contractorName: string | null;
  readonly jobTitle: string | null;
  readonly jobGrade: string | null;
};

export type ApprovalLineTemplate = {
  readonly templateId: string;
  readonly name: string;
  readonly members: readonly { readonly identityId: string; readonly sequence: number }[];
  readonly version: number;
};

export type ApprovalLineDesignerSnapshot = {
  readonly people: readonly ApprovalDirectoryPerson[];
  readonly templates: readonly ApprovalLineTemplate[];
  readonly revision: string;
  readonly completeness: 'complete';
};

export type ApprovalLineTemplateCommand = {
  readonly scope: ApprovalScope;
  readonly operation: 'upsert' | 'delete';
  readonly templateId: string;
  readonly commandId: string;
  readonly idempotencyKey: string;
  readonly expectedVersion: number;
  readonly name?: string;
  readonly members?: readonly { readonly identityId: string }[];
};

export interface AuthoritativeApprovalPorts {
  loadInbox(
    query: ApprovalHubQuery,
    cursor?: { readonly requestedAt: string; readonly requestId: string } | null,
  ): Promise<ApprovalWorkspacePage>;
  loadRequest(requestId: string, scope: ApprovalScope): Promise<ApprovalDocument | null>;
  submitDecision(
    document: ApprovalDocument,
    action: ApprovalDecision['action'],
    reason: string | null,
    inspectionReportReceiptId?: string,
  ): Promise<ApprovalAuditReceipt>;
  withdraw(document: ApprovalDocument, reason: string): Promise<ApprovalWithdrawalReceipt>;
  extendRouteAfterSafetyReview(
    document: ApprovalDocument,
    reason: string,
    reviewerIdentityIds: readonly string[],
  ): Promise<ApprovalRouteExtensionReceipt>;
  resubmit(
    document: ApprovalDocument,
    lineSelection: ApprovalLineSelection,
  ): Promise<ApprovalSubmissionReceipt>;
  loadIntakeDrafts(
    scope: ApprovalScope,
    cursor?: ApprovalIntakeDraftCursor | null,
  ): Promise<ApprovalIntakeDraftPage>;
  resumeIntakeDraft(
    draft: ApprovalIntakeDraft,
    lineSelection: ApprovalLineSelection,
  ): Promise<ApprovalSubmissionReceipt>;
  loadLineDesigner(scope: ApprovalScope): Promise<ApprovalLineDesignerSnapshot>;
  executeLineTemplate(command: ApprovalLineTemplateCommand): Promise<void>;
}

export type DecisionBlockCode =
  | 'request-terminal'
  | 'approval-line-invalid'
  | 'policy-missing'
  | 'policy-mismatch'
  | 'approval-not-required'
  | 'source-authority-missing'
  | 'source-revision-stale'
  | 'source-hash-stale'
  | 'source-receipt-expired'
  | 'decision-authority-missing'
  | 'cross-scope'
  | 'wrong-line-member'
  | 'self-approval'
  | 'separation-of-duties'
  | 'delegation-invalid'
  | 'return-reason-required'
  | 'duplicate-command'
  | 'replayed-idempotency-key';

export type DecisionValidation =
  | { readonly ok: true }
  | { readonly ok: false; readonly code: DecisionBlockCode; readonly message: string };

export type ReceiptValidation =
  { readonly ok: true } | { readonly ok: false; readonly message: string };

export const dailySafetyMeetingDefaultPolicy: ApprovalPolicy = {
  policyId: 'builtin:daily-safety-meeting:record',
  version: 1,
  sourceDomain: 'communications',
  documentKind: 'daily-safety-meeting',
  eventKind: 'record',
  scopeKinds: ['site'],
  mode: 'none',
  prohibitSelfApproval: true,
  requireDistinctReviewerAndApprover: true,
  dueHours: null,
  validFrom: '1970-01-01T00:00:00.000Z',
  validUntil: null,
};

export function defaultPolicyForEvent(input: {
  sourceDomain: string;
  documentKind: string;
  eventKind: string;
}): ApprovalPolicy | null {
  if (
    input.sourceDomain === dailySafetyMeetingDefaultPolicy.sourceDomain &&
    input.documentKind === dailySafetyMeetingDefaultPolicy.documentKind &&
    input.eventKind === dailySafetyMeetingDefaultPolicy.eventKind
  ) {
    return dailySafetyMeetingDefaultPolicy;
  }
  return null;
}

export function scopeKey(scope: ApprovalScope): string {
  if (scope.kind === 'tenant') return `tenant:${scope.tenantId}`;
  if (scope.kind === 'site') return `site:${scope.tenantId}:${scope.siteId}`;
  return `contractor:${scope.tenantId}:${scope.siteId}:${scope.contractorId}`;
}

export function isOverdue(request: ApprovalRequest, now: Date): boolean {
  return (
    (request.status === 'requested' || request.status === 'reviewing') &&
    Date.parse(request.dueAt) < now.getTime()
  );
}

export function filterInbox(
  requests: readonly ApprovalRequest[],
  filter: ApprovalInboxFilter,
  actorIdentityId: string,
  now: Date,
): readonly ApprovalRequest[] {
  return requests.filter((request) => {
    const activeMember = request.line.find(
      (member) =>
        member.sequence === request.activeSequence && member.identityId === actorIdentityId,
    );
    if (filter === 'my-pending')
      return Boolean(activeMember) && ['requested', 'reviewing'].includes(request.status);
    if (filter === 'requested-by-me') return request.requestedByIdentityId === actorIdentityId;
    if (filter === 'completed') return ['approved', 'withdrawn'].includes(request.status);
    if (filter === 'returned') return request.status === 'returned';
    if (filter === 'overdue') return isOverdue(request, now);
    return true;
  });
}

function fail(code: DecisionBlockCode, message: string): DecisionValidation {
  return { ok: false, code, message };
}

function policyMatches(request: ApprovalRequest, policy: ApprovalPolicy, now: Date): boolean {
  const timestamp = now.getTime();
  return (
    policy.policyId === request.policyId &&
    policy.version === request.policyVersion &&
    policy.sourceDomain === request.source.sourceDomain &&
    policy.documentKind === request.source.documentKind &&
    policy.eventKind === request.eventKind &&
    policy.scopeKinds.includes(request.source.scope.kind) &&
    Date.parse(policy.validFrom) <= timestamp &&
    (policy.validUntil === null || timestamp < Date.parse(policy.validUntil))
  );
}

export function validateApprovalLine(request: ApprovalRequest): readonly string[] {
  const errors: string[] = [];
  const requesterMembers = request.line.filter((member) => member.role === 'requester');
  if (
    requesterMembers.length !== 1 ||
    requesterMembers[0]?.identityId !== request.requestedByIdentityId
  ) {
    errors.push('결재선에는 요청자와 일치하는 기안자가 정확히 한 명이어야 합니다.');
  }
  if (!request.line.some((member) => member.role === 'approver')) {
    errors.push('결재선에는 최종 결재자가 한 명 이상 있어야 합니다.');
  }
  const memberIds = request.line.map((member) => member.lineMemberId);
  const sequences = request.line.map((member) => member.sequence);
  if (
    new Set(memberIds).size !== memberIds.length ||
    new Set(sequences).size !== sequences.length
  ) {
    errors.push('결재선 구성원 ID와 순서는 중복될 수 없습니다.');
  }
  if (
    request.activeSequence !== null &&
    !request.line.some(
      (member) => member.sequence === request.activeSequence && member.role !== 'requester',
    )
  ) {
    errors.push('활성 순서는 검토자 또는 결재자를 가리켜야 합니다.');
  }
  return errors;
}

function delegationMatches(
  request: ApprovalRequest,
  member: ApprovalLineMember,
  delegation: DelegationAuthority,
  actorIdentityId: string,
  now: Date,
): boolean {
  const timestamp = now.getTime();
  return (
    delegation.revokedAt === null &&
    delegation.delegateIdentityId === actorIdentityId &&
    delegation.delegatorIdentityId === member.identityId &&
    delegation.allowedRoles.includes(member.role as Exclude<ApprovalLineRole, 'requester'>) &&
    delegation.sourceDomains.includes(request.source.sourceDomain) &&
    delegation.documentKinds.includes(request.source.documentKind) &&
    delegation.eventKinds.includes(request.eventKind) &&
    scopeKey(delegation.scope) === scopeKey(request.source.scope) &&
    Date.parse(delegation.validFrom) <= timestamp &&
    timestamp < Date.parse(delegation.validUntil)
  );
}

export function validateDecision(input: {
  request: ApprovalRequest;
  policy: ApprovalPolicy | null;
  sourceAuthority: SourceAuthorityReceipt | null;
  decisionAuthority: DecisionAuthorityReceipt | null;
  decision: ApprovalDecision;
  priorReceipts: readonly ApprovalAuditReceipt[];
  now: Date;
}): DecisionValidation {
  const { request, policy, sourceAuthority, decisionAuthority, decision, priorReceipts, now } =
    input;
  if (!['requested', 'reviewing'].includes(request.status))
    return fail('request-terminal', '이미 종결되거나 반려된 결재 요청입니다.');
  if (validateApprovalLine(request).length > 0)
    return fail('approval-line-invalid', '결재선 구성이 유효하지 않습니다.');
  if (!policy) return fail('policy-missing', '이 이벤트에 적용할 승인 정책이 없습니다.');
  if (!policyMatches(request, policy, now))
    return fail('policy-mismatch', '결재 요청과 현재 유효한 정책이 일치하지 않습니다.');
  if (policy.mode === 'none')
    return fail('approval-not-required', '이 이벤트는 승인 대상이 아닙니다.');
  if (!sourceAuthority)
    return fail('source-authority-missing', '원본 화면에서 문서 확인 응답을 받지 못했습니다.');
  if (sourceAuthority.source.revisionId !== request.source.revisionId)
    return fail('source-revision-stale', '요청 후 원본 문서가 변경되었습니다. 다시 요청해 주세요.');
  if (
    sourceAuthority.source.contentHash !== request.source.contentHash ||
    decision.expectedContentHash !== request.source.contentHash
  )
    return fail('source-hash-stale', '요청 당시 문서 내용과 현재 원본 내용이 다릅니다.');
  if (Date.parse(sourceAuthority.expiresAt) <= now.getTime())
    return fail('source-receipt-expired', '원본 문서 확인 응답이 만료되었습니다.');
  if (!decisionAuthority)
    return fail('decision-authority-missing', '서버가 확인한 결재 권한이 없습니다.');
  if (
    scopeKey(request.source.scope) !== scopeKey(sourceAuthority.source.scope) ||
    scopeKey(request.source.scope) !== scopeKey(decisionAuthority.scope)
  )
    return fail('cross-scope', '다른 법인·사업장·협력사 범위의 결재는 처리할 수 없습니다.');
  const member = request.line.find(
    (candidate) =>
      candidate.lineMemberId === decisionAuthority.lineMemberId &&
      candidate.sequence === request.activeSequence,
  );
  if (!member || member.role === 'requester')
    return fail('wrong-line-member', '현재 활성 결재선의 처리자가 아닙니다.');
  if (
    decision.requestId !== request.requestId ||
    decision.actorIdentityId !== decisionAuthority.identityId ||
    decision.expectedRevisionId !== request.source.revisionId ||
    decision.expectedPolicyId !== policy.policyId ||
    decision.expectedPolicyVersion !== policy.version ||
    decision.sourceAuthorityReceiptId !== sourceAuthority.receiptId ||
    decision.decisionAuthorityReceiptId !== decisionAuthority.receiptId
  )
    return fail(
      'decision-authority-missing',
      '요청 정보가 서버에서 확인한 권한이나 문서 내용과 일치하지 않습니다.',
    );
  if (Date.parse(decisionAuthority.expiresAt) <= now.getTime())
    return fail('decision-authority-missing', '결재 권한 확인 응답이 만료되었습니다.');
  if (
    policy.prohibitSelfApproval &&
    (request.requestedByIdentityId === member.identityId ||
      request.requestedByIdentityId === decisionAuthority.identityId)
  )
    return fail('self-approval', '기안자는 자신의 문서를 결재할 수 없습니다.');
  if (policy.requireDistinctReviewerAndApprover) {
    const reviewerIds = request.line
      .filter((candidate) => candidate.role === 'reviewer')
      .map((candidate) => candidate.identityId);
    const approverIds = request.line
      .filter((candidate) => candidate.role === 'approver')
      .map((candidate) => candidate.identityId);
    const actualActorCrossesRoles =
      (member.role === 'approver' && reviewerIds.includes(decisionAuthority.identityId)) ||
      (member.role === 'reviewer' && approverIds.includes(decisionAuthority.identityId));
    if (
      reviewerIds.some((identityId) => approverIds.includes(identityId)) ||
      actualActorCrossesRoles
    )
      return fail('separation-of-duties', '검토자와 최종 결재자는 서로 다른 사람이어야 합니다.');
  }
  if (decisionAuthority.identityId !== member.identityId) {
    if (
      !decisionAuthority.delegation ||
      !delegationMatches(
        request,
        member,
        decisionAuthority.delegation,
        decisionAuthority.identityId,
        now,
      )
    )
      return fail('delegation-invalid', '유효한 범위·기간·업무의 대결 위임 증빙이 없습니다.');
  } else if (decisionAuthority.delegation !== null) {
    return fail('delegation-invalid', '직접 결재 권한과 대결 증빙을 동시에 사용할 수 없습니다.');
  }
  if (decision.action === 'return' && !decision.reason?.trim())
    return fail('return-reason-required', '반려 사유를 입력해야 합니다.');
  if (priorReceipts.some((receipt) => receipt.commandId === decision.commandId))
    return fail('duplicate-command', '이미 처리된 결재 명령입니다.');
  if (priorReceipts.some((receipt) => receipt.idempotencyKey === decision.idempotencyKey))
    return fail('replayed-idempotency-key', '이미 사용된 요청 키입니다.');
  return { ok: true };
}

/** A successful HTTP response is not enough: the returned server receipt must bind the exact command. */
export function validateAuditReceipt(
  receipt: ApprovalAuditReceipt,
  decision: ApprovalDecision,
  request: ApprovalRequest,
  decisionAuthority: DecisionAuthorityReceipt,
): ReceiptValidation {
  const hasLaterLineMember = request.line.some(
    (member) => request.activeSequence !== null && member.sequence > request.activeSequence,
  );
  const expectedResult: ApprovalRequestStatus =
    decision.action === 'return' ? 'returned' : hasLaterLineMember ? 'reviewing' : 'approved';
  const actedAs = decisionAuthority.delegation?.delegatorIdentityId ?? decisionAuthority.identityId;
  const delegationId = decisionAuthority.delegation?.delegationId ?? null;
  if (
    receipt.requestId !== request.requestId ||
    receipt.commandId !== decision.commandId ||
    receipt.idempotencyKey !== decision.idempotencyKey ||
    receipt.action !== decision.action ||
    receipt.actorIdentityId !== decision.actorIdentityId ||
    receipt.actedAsIdentityId !== actedAs ||
    receipt.delegationId !== delegationId ||
    receipt.sourceRevisionId !== request.source.revisionId ||
    receipt.sourceContentHash !== request.source.contentHash ||
    receipt.policyId !== request.policyId ||
    receipt.policyVersion !== request.policyVersion ||
    receipt.previousStatus !== request.status ||
    receipt.resultingStatus !== expectedResult ||
    !Number.isSafeInteger(receipt.serverSequence) ||
    receipt.serverSequence <= 0 ||
    !receipt.auditEventId ||
    !receipt.receiptId
  ) {
    return { ok: false, message: '서버 영수증이 요청한 결재 행위와 정확히 일치하지 않습니다.' };
  }
  return { ok: true };
}
import type { RemediationReport } from './remediation-report';
